import React, { useState, useEffect } from 'react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import PharmacyApp from './pages/pharmacy/PharmacyApp';
import MedicielImportPage from './pages/pharmacy/MedicielImportPage';
import StaffPortalPage from './pages/StaffPortalPage';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import SkinAnalysisPage from './pages/SkinAnalysisPage';
import ExpressDiagnosisPage from './pages/ExpressDiagnosisPage';
import DiagnosisResultsPage from './pages/DiagnosisResultsPage';
import ProductsPage from './pages/ProductsPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import SadaStockStandalonePage from './pages/SadaStockStandalonePage';
import B2BStandalonePage from './pages/B2BStandalonePage';
import StaffAcademyPage from './pages/StaffAcademyPage';
import AgentDashboard from './pages/AgentDashboard';
import SaleRecordPage from './pages/SaleRecordPage';
import StatsPage from './pages/StatsPage';
import ClientsPage from './pages/ClientsPage';
import HelpPage from './pages/HelpPage';
import ModuleSelector from './pages/ModuleSelector';
import SADAHealthApp from './pages/SADAHealthApp';
import SADASSURApp from './pages/SADASSURApp';
import BabyDiagnosisPage from './pages/BabyDiagnosisPage';
import BottomNavigation from './components/BottomNavigation';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import DebugBanner from './components/DebugBanner';
import VerifyValidationPage from './pages/VerifyValidationPage';
import PublicDeliveryNotePage from './pages/PublicDeliveryNotePage';
import PublicCotationPage from './pages/PublicCotationPage';
import PublicDocumentPage from './pages/PublicDocumentPage';
import './App.css';

// Détection de la route publique de vérification d'étiquette : /v/RCP-... ou /v/RCP-.../token
// Pas de react-router → on lit window.location.pathname au boot.
const VERIFY_PATH_MATCH = (() => {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/v\/(RCP-\d{6}-\d{4}-[A-Z]{2})(?:\/([a-f0-9]{8}))?$/i);
  if (!m) return null;
  return { validationNumber: m[1].toUpperCase(), token: (m[2] || '').toLowerCase() };
})();

// Route publique Bons de livraison B2B : /bl/<token>
const BL_PATH_MATCH = (() => {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/bl\/([A-Za-z0-9_-]{16,})\/?$/);
  if (!m) return null;
  return { token: m[1] };
})();

// Route publique Validation cotation B2B : /cotation/<token>
const COT_PATH_MATCH = (() => {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/cotation\/([A-Za-z0-9_-]{16,})\/?$/);
  if (!m) return null;
  return { token: m[1] };
})();

// Route publique Document B2B (facture / ticket) : /document/<token>
const DOC_PATH_MATCH = (() => {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/document\/([A-Za-z0-9_-]{16,})\/?$/);
  if (!m) return null;
  return { token: m[1] };
})();

const AppContent = () => {
  const { user, loading, logout } = useAuth();
  const { language } = useLanguage();
  // ALWAYS start with public pharmacy website
  const [appMode, setAppMode] = useState('public'); // 'public', 'portal', 'landing', or 'staff'
  const [currentPage, setCurrentPage] = useState('home');
  const [currentDiagnosis, setCurrentDiagnosis] = useState(null);
  const [staffAuthenticated, setStaffAuthenticated] = useState(false); // Track if staff code was entered
  const [selectedModule, setSelectedModule] = useState(null); // 'skincare' or 'health'
  const [adminInitialTab, setAdminInitialTab] = useState(null);

  // Deep link `?goto=reception-pending` (depuis email validateur)
  // Route automatiquement vers Réceptions si l'utilisateur est déjà connecté.
  useEffect(() => {
    if (!user) return;
    const goto = new URLSearchParams(window.location.search).get('goto');
    if (goto !== 'reception-pending') return;
    const isAgentOrAdmin = user?.role === 'agent' || user?.role === 'admin' ||
      user?.role === 'manager' || user?.role === 'supervisor' ||
      user?.role === 'accountant' || user?.is_admin;
    if (!isAgentOrAdmin) return;
    setSelectedModule('reception');
    setAdminInitialTab('reception');
    setCurrentPage('admin');
    setAppMode('staff');
    setStaffAuthenticated(true);
  }, [user]);

  // Deep link `/admin` ou `/admin?tab=...` — accès direct à SADAdmin pour les admins
  // déjà connectés (utile pour bookmark, debug, tests automatisés). Pour les autres
  // rôles ou les utilisateurs non connectés, on retombe sur le flux public normal.
  useEffect(() => {
    if (!user) return;
    const path = window.location.pathname || '';
    if (path !== '/admin' && !path.startsWith('/admin/')) return;
    if (!(user?.role === 'admin' || user?.is_admin)) return;
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab) setAdminInitialTab(tab);
    setCurrentPage('admin');
    setAppMode('staff');
    setStaffAuthenticated(true);
  }, [user]);

  // ROUTE PUBLIQUE — Vérification d'étiquette /v/RCP-XXX
  // Doit être traitée AVANT toute logique d'auth (page publique sans login).
  // Placée après tous les hooks pour respecter la "rules of hooks".
  if (VERIFY_PATH_MATCH) {
    return (
      <>
        <VerifyValidationPage
          validationNumber={VERIFY_PATH_MATCH.validationNumber}
          token={VERIFY_PATH_MATCH.token}
        />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // ROUTE PUBLIQUE — Bon de livraison B2B /bl/<token>
  if (BL_PATH_MATCH) {
    return (
      <>
        <PublicDeliveryNotePage token={BL_PATH_MATCH.token} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // ROUTE PUBLIQUE — Validation cotation B2B /cotation/<token>
  if (COT_PATH_MATCH) {
    return (
      <>
        <PublicCotationPage token={COT_PATH_MATCH.token} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // ROUTE PUBLIQUE — Document B2B (facture / ticket) /document/<token>
  if (DOC_PATH_MATCH) {
    return (
      <>
        <PublicDocumentPage token={DOC_PATH_MATCH.token} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-[#2E7D32] animate-spin" />
      </div>
    );
  }

  // PUBLIC WEBSITE MODE - Always show by default
  if (appMode === 'public') {
    return (
      <>
        <PharmacyApp onStaffLogin={() => setAppMode('portal')} />
        <DebugBanner />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // STAFF PORTAL - Code entry + Useful links
  if (appMode === 'portal') {
    return (
      <>
        <StaffPortalPage 
          onBack={() => {
            setAppMode('public');
            setStaffAuthenticated(false);
          }}
          onAccessSADASkinCare={() => {
            // Clear any previous session when entering with code
            localStorage.removeItem('dermascan_token');
            localStorage.removeItem('dermascan_user');
            setStaffAuthenticated(true);
            setAppMode('staff'); // Go directly to auth page
            setCurrentPage('auth');
          }}
          onAccessB2B={() => {
            // B2B accessible directement si l'utilisateur a déjà un JWT
            // (le bouton n'apparaît que dans ce cas, garde côté StaffPortalPage).
            // Pas de removal du token : on garde la session JWT en cours.
            setStaffAuthenticated(true);
            setAppMode('staff');
            setCurrentPage('b2b');
          }}
          onAccessSADASSUR={() => {
            // First login, then go to SADASSUR
            localStorage.removeItem('dermascan_token');
            localStorage.removeItem('dermascan_user');
            setStaffAuthenticated(true);
            setAppMode('staff');
            setCurrentPage('auth');
            // Store target module to redirect after login
            sessionStorage.setItem('redirect_to', 'sadassur');
          }}
        />
        <DebugBanner />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // SADASSUR - Insurance Management Module
  if (appMode === 'sadassur') {
    return (
      <>
        <SADASSURApp />
        <DebugBanner />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // STAFF MODE - requires authentication
  // Redirect to auth if not logged in
  if (appMode === 'staff' && !user && currentPage !== 'auth') {
    if (!staffAuthenticated) {
      setAppMode('portal');
      return null;
    }
    return (
      <>
        <AuthPage 
          onSuccess={() => {
            setTimeout(() => {
              // Check if we should redirect to SADASSUR
              const redirectTo = sessionStorage.getItem('redirect_to');
              if (redirectTo === 'sadassur') {
                sessionStorage.removeItem('redirect_to');
                setAppMode('sadassur');
                return;
              }
              
              const newUser = JSON.parse(localStorage.getItem('dermascan_user') || '{}');
              const isAgentOrAdmin = newUser?.role === 'agent' || newUser?.role === 'admin' || newUser?.role === 'manager' || newUser?.role === 'supervisor' || newUser?.role === 'accountant' || newUser?.is_admin;
              if (isAgentOrAdmin) {
                setCurrentPage('module-selector');
              } else {
                setCurrentPage('analysis');
              }
            }, 100);
          }}
          onBack={() => {
            sessionStorage.removeItem('redirect_to');
            setAppMode('portal');
            setStaffAuthenticated(false);
          }}
        />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  // User is logged in
  if (user && currentPage === 'auth') {
    const isAgentOrAdmin = user?.role === 'agent' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'supervisor' || user?.role === 'accountant' || user?.is_admin;
    // ── Deep link `?goto=reception-pending` (depuis email) → ouvre directement Réceptions ──
    if (typeof window !== 'undefined') {
      const goto = new URLSearchParams(window.location.search).get('goto');
      if (goto === 'reception-pending' && isAgentOrAdmin) {
        setSelectedModule('reception');
        setAdminInitialTab('reception');
        setCurrentPage('admin');
        return null;
      }
    }
    if (isAgentOrAdmin) {
      // Show module selector for agents
      setCurrentPage('module-selector');
    } else {
      setCurrentPage('analysis');
    }
    return null;
  }

  const isAgent = user?.role === 'agent' || user?.role === 'admin' || user?.role === 'manager' || user?.role === 'supervisor' || user?.role === 'accountant' || user?.is_admin;

  const handleStartDiagnosis = () => {
    setCurrentPage('analysis');
  };

  const handleDiagnosisComplete = (diagnosis) => {
    setCurrentDiagnosis(diagnosis);
    setCurrentPage('results');
  };

  const handleViewDiagnosis = (diagnosis) => {
    setCurrentDiagnosis(diagnosis);
    setCurrentPage('results');
  };

  const handleRecordSale = () => {
    if (currentDiagnosis) {
      setCurrentPage('sale-record');
    }
  };

  const handleSaleComplete = () => {
    setCurrentDiagnosis(null);
    setCurrentPage(isAgent ? 'agent-dashboard' : 'dashboard');
  };

  const handleLogout = () => {
    logout();
    setAppMode('public');
    setCurrentPage('home');
    setStaffAuthenticated(false);
  };

  const getDefaultDashboard = () => {
    if (isAgent) return 'agent-dashboard';
    return 'dashboard';
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'auth':
        return (
          <AuthPage 
            onSuccess={() => {
              setTimeout(() => {
                const newUser = JSON.parse(localStorage.getItem('dermascan_user') || '{}');
                const isAgentOrAdmin = newUser?.role === 'agent' || newUser?.role === 'admin' || newUser?.role === 'manager' || newUser?.role === 'supervisor' || newUser?.role === 'accountant' || newUser?.is_admin;
                if (isAgentOrAdmin) {
                  setCurrentPage('module-selector');
                } else {
                  setCurrentPage('analysis');
                }
              }, 100);
            }}
            onBack={() => setAppMode('public')}
          />
        );
      
      case 'analysis':
        return (
          <SkinAnalysisPage 
            onBack={() => setCurrentPage(getDefaultDashboard())}
            onComplete={handleDiagnosisComplete}
          />
        );

      case 'express-diagnosis':
        return (
          <ExpressDiagnosisPage onBack={() => setCurrentPage(getDefaultDashboard())} />
        );
      
      case 'results':
        return (
          <DiagnosisResultsPage 
            diagnosis={currentDiagnosis}
            onBack={() => setCurrentPage(getDefaultDashboard())}
            onViewProducts={() => setCurrentPage('products')}
            onSave={() => setCurrentPage(getDefaultDashboard())}
            onRecordSale={isAgent ? handleRecordSale : null}
            isAgent={isAgent}
          />
        );
      
      case 'sale-record':
        return (
          <SaleRecordPage
            diagnosis={currentDiagnosis}
            onBack={() => setCurrentPage('results')}
            onComplete={handleSaleComplete}
          />
        );
      
      case 'products':
        return (
          <ProductsPage 
            onBack={() => currentDiagnosis ? setCurrentPage('results') : setCurrentPage(getDefaultDashboard())}
            diagnosis={currentDiagnosis}
            onSaleComplete={handleSaleComplete}
          />
        );
      
      case 'dashboard':
        return (
          <DashboardPage 
            onBack={() => setAppMode('public')}
            onNewDiagnosis={() => setCurrentPage('analysis')}
            onExpressDiagnosis={() => setCurrentPage('express-diagnosis')}
            onViewDiagnosis={handleViewDiagnosis}
            onBabyDiagnosis={() => setCurrentPage('baby-diagnosis')}
          />
        );
      
      case 'agent-dashboard':
        return (
          <AgentDashboard
            onNewDiagnosis={() => setCurrentPage('analysis')}
            onExpressDiagnosis={() => setCurrentPage('express-diagnosis')}
            onViewDiagnosis={handleViewDiagnosis}
            onViewStats={() => setCurrentPage('stats')}
            onViewClients={() => setCurrentPage('clients')}
            onViewHelp={() => setCurrentPage('help')}
            onLogout={handleLogout}
            onBackToPublic={() => setAppMode('public')}
            onSwitchToHealth={() => {
              setSelectedModule('health');
              setCurrentPage('health');
            }}
            onSwitchToRayons={() => {
              setSelectedModule('inventory');
              const role = user?.role;
              const isAdminOrManager = user?.is_admin || role === 'admin' || role === 'manager';
              if (isAdminOrManager) {
                setAdminInitialTab('inventaire');
                setCurrentPage('admin');
              } else {
                setCurrentPage('sadastock-standalone');
              }
            }}
            onSwitchToReception={() => {
              setSelectedModule('reception');
              setAdminInitialTab('reception');
              setCurrentPage('admin');
            }}
            onBabyDiagnosis={() => setCurrentPage('baby-diagnosis')}
          />
        );
      
      case 'baby-diagnosis':
        return (
          <BabyDiagnosisPage
            onBack={() => setCurrentPage(getDefaultDashboard())}
            onComplete={(result) => {
              // Store baby diagnosis result and navigate to products if needed
              setCurrentDiagnosis(result);
              setCurrentPage('products');
            }}
          />
        );
      
      case 'clients':
        return (
          <ClientsPage
            onBack={() => setCurrentPage('agent-dashboard')}
            onSelectClient={(client) => {
              console.log('Selected client:', client);
            }}
          />
        );
      
      case 'stats':
        return (
          <StatsPage
            onBack={() => setCurrentPage(getDefaultDashboard())}
          />
        );
      
      case 'admin':
        return (
          <AdminPage 
            onBack={() => { setAdminInitialTab(null); setCurrentPage(getDefaultDashboard()); }}
            onNavigateToImport={() => setCurrentPage('import-mediciel')}
            initialMainTab={adminInitialTab}
          />
        );
      case 'sadastock-standalone':
        return (
          <SadaStockStandalonePage
            onBack={() => setCurrentPage('module-selector')}
          />
        );

      case 'b2b':
        return (
          <B2BStandalonePage
            onBack={() => setCurrentPage('module-selector')}
          />
        );

      case 'academy':
        return (
          <StaffAcademyPage
            onBack={() => setCurrentPage('module-selector')}
          />
        );
      
      case 'import-mediciel':
        return (
          <MedicielImportPage 
            onBack={() => setCurrentPage('admin')}
          />
        );
      
      case 'help':
        return (
          <HelpPage 
            onBack={() => setCurrentPage(getDefaultDashboard())}
          />
        );
      
      case 'module-selector':
        return (
          <ModuleSelector
            userName={user?.name}
            user={user}
            onBack={handleLogout}
            onSelectModule={(module) => {
              setSelectedModule(module);
              if (module === 'skincare') {
                setCurrentPage('agent-dashboard');
              } else if (module === 'health') {
                setCurrentPage('health');
              } else if (module === 'insurance') {
                setAppMode('sadassur');
              } else if (module === 'inventory') {
                // Admin / Manager → AdminPage avec onglet Inventaire (mini-nav admin/manager)
                // Agents (agent / supervisor) → page standalone SADAStock
                const role = user?.role;
                const isAdminOrManager = user?.is_admin || role === 'admin' || role === 'manager';
                if (isAdminOrManager) {
                  setAdminInitialTab('inventaire');
                  setCurrentPage('admin');
                } else {
                  setCurrentPage('sadastock-standalone');
                }
              } else if (module === 'reception') {
                setAdminInitialTab('reception');
                setCurrentPage('admin');
              } else if (module === 'sadadmin') {
                // SADAdmin : ouvre AdminPage avec catégorie « SkinCare » par défaut
                setAdminInitialTab('products');
                setCurrentPage('admin');
              } else if (module === 'b2b') {
                // B2B Cliniques : module métier autonome, parallèle aux autres.
                // Pas de gateway code — accès direct selon rôle.
                setCurrentPage('b2b');
              } else if (module === 'academy') {
                // SADA Academy : accessible à tous les utilisateurs connectés.
                setCurrentPage('academy');
              }
            }}
          />
        );
      
      case 'health':
        return (
          <SADAHealthApp
            onBack={() => setCurrentPage('module-selector')}
            onSwitchModule={() => {
              setSelectedModule('skincare');
              setCurrentPage('agent-dashboard');
            }}
            onSwitchToRayons={() => {
              setSelectedModule('inventory');
              const role = user?.role;
              const isAdminOrManager = user?.is_admin || role === 'admin' || role === 'manager';
              if (isAdminOrManager) {
                setAdminInitialTab('inventaire');
                setCurrentPage('admin');
              } else {
                setCurrentPage('sadastock-standalone');
              }
            }}
            onSwitchToReception={() => {
              setSelectedModule('reception');
              setAdminInitialTab('reception');
              setCurrentPage('admin');
            }}
          />
        );
      
      default:
        return (
          <AgentDashboard
            onNewDiagnosis={() => setCurrentPage('analysis')}
            onExpressDiagnosis={() => setCurrentPage('express-diagnosis')}
            onViewDiagnosis={handleViewDiagnosis}
            onViewStats={() => setCurrentPage('stats')}
            onViewClients={() => setCurrentPage('clients')}
            onViewHelp={() => setCurrentPage('help')}
            onLogout={handleLogout}
            onBackToPublic={() => setAppMode('public')}
          />
        );
    }
  };

  const showBottomNav = user && ['dashboard', 'agent-dashboard', 'products', 'admin', 'stats'].includes(currentPage);

  return (
    <div className="App">
      {renderPage()}
      {showBottomNav && (
        <BottomNavigation 
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          isAdmin={user?.is_admin || user?.role === 'admin'}
          isAgent={isAgent}
          userRole={user?.role}
        />
      )}
      <PWAInstallPrompt language={language} />
      <DebugBanner />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
